import { PopperProps } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import React from 'react'
import { useDispatch } from 'react-redux'
import SvgReceiptAttached from 'src/components/Svgs/SvgReceiptAttached'
import SvgReceiptMissing from 'src/components/Svgs/SvgReceiptMissing'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import {
  TransactionDetailModel,
  TransactionDetailResponse,
} from 'src/models/transaction.models'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useRequestConfig } from 'src/queries/queries.utils'
import { usePatchTransaction } from 'src/queries/transactions.queries'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

interface ReceiptsTooltipProps {
  count?: number
  placement: PopperProps['placement']
  isMissing?: boolean
  attachment?: ReceiptAttachmentModel
  transactionId?: string
}

const ReceiptsTooltip: React.FC<ReceiptsTooltipProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()
  const { mutate: patchTransaction } = usePatchTransaction()

  let message
  if (props.count !== undefined) {
    message = translate(
      props.count === 1 ? '${count} receipt' : '${count} receipts',
      { count: props.count.toString() },
    )
  }
  if (props.isMissing) {
    message = translate('Receipt required')
  }

  const handleOpenAttachment = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault()
    event.stopPropagation()
    if (props.isMissing) {
      return
    }
    const openAttachmentPopup = (
      attachment: ReceiptAttachmentModel,
      transactionId?: string,
      receiptAttachmentIds?: string[],
    ): void => {
      const deleteAction =
        transactionId !== undefined && receiptAttachmentIds !== undefined
          ? (): void => {
              patchTransaction({
                transactionId,
                body: {
                  receiptAttachmentIds: receiptAttachmentIds.filter(
                    (attachmentId) => attachmentId !== attachment.id,
                  ),
                },
              })
            }
          : undefined

      dispatch(createSetIsAttachmentPopupOpen(true, deleteAction, attachment))
    }

    const fetchTransactionDetail = async (
      transactionId: string,
    ): Promise<TransactionDetailModel | undefined> => {
      try {
        const response = await queryClient.fetchQuery({
          queryKey: transactionsQueryKeys.detail(transactionId),
          queryFn: () =>
            axios.get<TransactionDetailResponse>(
              `/transactions/${transactionId}`,
              requestConfig,
            ),
          retry: false,
        })
        return new TransactionDetailModel(response.data)
      } catch {
        return undefined
      }
    }

    if (props.attachment !== undefined) {
      const transactionId =
        props.transactionId ?? props.attachment.transactionId
      if (transactionId !== undefined) {
        const cached = queryClient.getQueryData<TransactionDetailResponse>(
          transactionsQueryKeys.detail(transactionId),
        )
        if (cached !== undefined) {
          const detail = new TransactionDetailModel(cached)
          openAttachmentPopup(
            props.attachment,
            transactionId,
            detail.receiptAttachments.map((attachment) => attachment.id),
          )
          return
        }
        const detail = await fetchTransactionDetail(transactionId)
        if (detail !== undefined) {
          openAttachmentPopup(
            props.attachment,
            transactionId,
            detail.receiptAttachments.map((attachment) => attachment.id),
          )
          return
        }
      }
      openAttachmentPopup(props.attachment)
      return
    }

    if (props.transactionId !== undefined) {
      const detail = await fetchTransactionDetail(props.transactionId)
      const firstAttachment = detail?.receiptAttachments[0]
      if (detail !== undefined && firstAttachment !== undefined) {
        openAttachmentPopup(
          firstAttachment,
          props.transactionId,
          detail.receiptAttachments.map((attachment) => attachment.id),
        )
      }
    }
  }

  const receiptIcon = props.isMissing ? (
    <SvgReceiptMissing
      css={css`
        transform: scale(calc(20 / 24));
      `}
    />
  ) : (
    <SvgReceiptAttached
      css={css`
        transform: scale(calc(20 / 24));
      `}
    />
  )

  const tooltipIcon = (
    <TooltipIcon
      placement={props.placement}
      icon={receiptIcon}
      iconTestId={
        props.isMissing ? 'missingReceiptsIcon' : 'receiptsAttachedIcon'
      }
    >
      {message}
    </TooltipIcon>
  )

  const canOpenAttachment =
    !props.isMissing &&
    (props.attachment !== undefined || props.transactionId !== undefined)

  return canOpenAttachment ? (
    <button
      type="button"
      onClick={(event): void => {
        void handleOpenAttachment(event)
      }}
      css={css`
        border: none;
        padding: 0;
        margin: 0;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
      `}
    >
      {tooltipIcon}
    </button>
  ) : (
    tooltipIcon
  )
}

export default ReceiptsTooltip
